import { select } from "three/src/nodes/TSL.js";
import { runWhenGlobalIdle, ThrottledDelayer } from "../../../base/common/async.js";
import { IAttachment } from "../../../base/common/attachment.js";
import { CancellationToken } from "../../../base/common/cancellation.js";
import { Emitter } from "../../../base/common/events.js";
import { Disposable, IDisposable } from "../../../base/common/lifecycle.js";
import { CommandsRegistry, ICommandService } from "../../commands/common/commands.js";
import { IConfigurationService } from "../../configuration/common/configuration.js";
import { IContextKeyService } from "../../contextkey/common/contextkey.js";
import { IInstantiationService } from "../../instantiation/common/instantiation.js";
import { ILogService } from "../../log/common/log.js";
import { IStorageService } from "../../storage/common/storage.js";
import { isHierarchyNode as isHierarchyNode, IHierarchyNode, NodeFlags, NodeChangedEvent as HierarchyNodeChangedEvent, IHierarchyService, ITreeProviderService, HierarchyChangedEvent, SelectedNodesChangedEvent } from "./hierarchy.js";
import { DeleteNodeCommand, NavigateHierarchyCommand, RenameNodeCommand, SelectNodeCommand, SetNodeExpandedCommand } from "./treeCommands.js";


export class HierarchyService extends Disposable implements IHierarchyService {
    _serviceBrand: undefined;

    private readonly _onDidChange = new Emitter<HierarchyChangedEvent>();
    readonly onDidChange = this._onDidChange.event;

    private readonly _onNodeAdded = new Emitter<IHierarchyNode>();
    readonly onNodeAdded = this._onNodeAdded.event;

    private readonly _onNodeRemoved = new Emitter<IHierarchyNode>();
    readonly onNodeRemoved = this._onNodeRemoved.event;

    private readonly _onNodeChanged = new Emitter<IHierarchyNode>();
    readonly onNodeChanged = this._onNodeChanged.event;

    private readonly _onSelectedNodesDidChange = new Emitter<SelectedNodesChangedEvent>();
    readonly onSelectedNodesDidChange = this._onSelectedNodesDidChange.event;


    constructor(
        @ILogService private readonly logger: ILogService,
        @IInstantiationService private readonly instantiationService: IInstantiationService,
        @ITreeProviderService private readonly treeProviderService: ITreeProviderService,
        @ICommandService private readonly commands: ICommandService,
        @IConfigurationService private readonly configurationService: IConfigurationService,
        @IContextKeyService private readonly contextKeyService: IContextKeyService,
        @IStorageService private readonly storageService: IStorageService,
    ) {
        super();


        this._register(CommandsRegistry.registerCommand(new RenameNodeCommand(this)));
        this._register(CommandsRegistry.registerCommand(new SelectNodeCommand(this, this._onSelectedNodesDidChange)));
        this._register(CommandsRegistry.registerCommand(new DeleteNodeCommand(this, this._onSelectedNodesDidChange)));
        this._register(CommandsRegistry.registerCommand(new SetNodeExpandedCommand(this)));
        this._register(CommandsRegistry.registerCommand(new NavigateHierarchyCommand(this, this.logger)));

        const anySelected = contextKeyService.createKey<boolean>("hierarchy.nodeSelected", false);
        this._register(this.onSelectedNodesDidChange(e => {
            anySelected.set(e.selection.length > 0)
        }))


        // Try to restore previous hierarchy state. 
        // We use the first node being added as a signal that the hierarchy is 'ready'
        const restoreDelayer = new ThrottledDelayer<void>(100);
        let disposeTimeout = -1;
        let previousSelected = new WeakSet<IHierarchyNode>();
        const restoreListener = this._register(this.onDidChange((evt) => {

            // Only run restore logic when the hierarchy changes
            if (!evt.hierarchy) return;

            // We use a simple timeout here because otherwise we might run into issues where this never gets disposed
            // E.g. when selected objects change rapidly within one frame
            if (disposeTimeout === -1) {
                disposeTimeout = window.setTimeout(() => {
                    restoreListener.dispose();
                }, 5000); // give the scene 5 seconds to load/stabilize
            }
            restoreDelayer.trigger(async () => {
                runWhenGlobalIdle(() => {
                    const selectedNodes = [];
                    for (const node of this.iterateNodes()) {
                        // Restore state
                        const expanded = SetNodeExpandedCommand.getStorageState(node, this.storageService);
                        if (typeof expanded === "boolean") node.expanded = expanded;
                        // @TODO: ids can change between reloads
                        const selected = SelectNodeCommand.IS_SELECTED(node.id, this.storageService);
                        if (typeof selected === "boolean") {
                            node.selected = selected;
                            if (selected) selectedNodes.push(node);
                        }
                    }
                    if (selectedNodes?.length) {
                        // check if any of the newly selected nodes are actually different from the previous selection (e.g. due to new nodes being added that were previously selected)
                        // We do this extra check here because if an object selected rapidly changes within one frame (every frame) the restoreListener might never dispose and we would end up firing selection changed events indefinitely which can cause performance issues where we accumulate memory over time
                        const selectionChanged = selectedNodes.some(n => !previousSelected.has(n));
                        if (selectionChanged) {
                            previousSelected = new WeakSet(selectedNodes);
                            this._onSelectedNodesDidChange.fire({ selection: selectedNodes });
                            restoreListener.dispose();
                            // Make sure selected nodes are visible by expanding their parents
                            selectedNodes.forEach(node => {
                                let parent = node.parent;
                                while (parent) {
                                    if (!parent.expanded) {
                                        parent.expanded = true;
                                    }
                                    parent = parent.parent;
                                }
                            });
                        }
                    }
                });
            })
        }));
    }

    __hackForceSelectionChangedEvent() {
        this._onSelectedNodesDidChange.fire({ selection: this.getSelectedNodes() });
    }

    notifyChanged() {
        this._onDidChange.fire({ hierarchy: this });
    }


    getSelectedNodes(): IHierarchyNode[] {
        const selected: IHierarchyNode[] = [];
        for (const node of this.iterateNodes()) {
            if (node.selected) {
                selected.push(node);
            }
        }
        return selected;
    }

    *iterateNodes(): Iterable<IHierarchyNode> {
        for (const node of this.roots) {
            if (node) {
                yield* this._traverse(node);
            }
        }
    }
    private * _traverse(node: IHierarchyNode): Iterable<IHierarchyNode> {
        yield node;
        for (const child of node.children) {
            yield* this._traverse(child);
        }
    }

    find(object: object | null | undefined): IHierarchyNode | null {
        if (!object) return null;
        if (this.lookupCacheReference.has(object)) {
            return this.lookupCacheReference.get(object)!;
        }
        return null;
    }

    findById(id: string): IHierarchyNode | null {
        if (this.lookupCacheId.has(id)) {
            return this.lookupCacheId.get(id)!;
        }
        return null;
    }

    async search(query: string | string[], maxResults: number = 100, token?: CancellationToken): Promise<IHierarchyNode[]> {
        const results: IHierarchyNode[] = [];

        if (typeof query === "string") {
            query = [query];
        }

        const querySegments = query.map(q => q.toLowerCase().trim());

        const maxPerFrame = 100;
        let frameProcessed = 0;

        for (const node of this.iterateNodes()) {

            if (token?.isCancellationRequested) {
                break;
            }

            if (results.length >= maxResults) {
                break;
            }
            if (++frameProcessed >= maxPerFrame) {
                frameProcessed = 0;
                await new Promise(resolve => setTimeout(resolve, 0));
            }

            let match = false;
            for (const searchTerm of querySegments) {
                if (match) break;
                if (searchTerm === "*" ||
                    node.name.toLowerCase().includes(searchTerm) ||
                    node.searchTags?.some(tag => tag.toLowerCase().includes(searchTerm)) ||
                    node.type.toLowerCase().includes(searchTerm)
                ) {
                    // console.log({ query, searchTerm, name: node.name, tahs: node.searchTags, type: node.type });
                    match = true;
                    results.push(node);
                }
            }
        }
        return results;
    }


    /**
     * The root nodes of the hierarchy.
     */
    readonly roots: IHierarchyNode[] = [];

    private _adding: Map<object, Promise<IHierarchyNode | null>> = new Map();

    async add(object: object, parent?: IHierarchyNode | object, token?: CancellationToken): Promise<IHierarchyNode | null> {

        // if (isHierarchyRootNode(object)) {
        //     this.roots.push(object);
        //     this._onNodeAdded.fire(object.root!);
        //     this._onDidChange.fire({ node: object.root! });
        //     return object.root;
        // }
        // if (isHierarchyNode(object)) {
        //     console.log(object, this.roots
        // }

        if (this._adding.has(object)) {
            const existingPromise = this._adding.get(object)!;
            return existingPromise;
        }

        const promise = this._add(object, token);
        this._adding.set(object, promise);
        try {
            const result = await promise;
            return result;
        } finally {
            this._adding.delete(object);
        }
    }

    private async _add(object: object, token: CancellationToken | undefined): Promise<IHierarchyNode | null> {

        try {

            // check if it already exists
            const existing = this.find(object);
            if (existing) {
                return existing;
            }

            const provider = this.treeProviderService.getProvider(object);
            if (!provider) {
                this.logger.warn("[HierarchyService] No hierarchy provider found for object...");
                return null;
            }
            const rootNode = await this.instantiationService.invokeFunction(a => provider.createTree(a, object));
            if (!rootNode) {
                this.logger.warn("Hierarchy provider failed to create tree");
                return null;
            }

            this.roots.push(rootNode);
            const node = rootNode;
            this._registerNode(node);

            this.logger.debug("HierarchyService: Added object to hierarchy:", object);

            // Fire onNodeAdded for root and ALL children
            this._fireNodeAddedRecursive(node);
            this._onDidChange.fire({ hierarchy: this });

            return node;
        } catch (error) {
            this.logger.error("Error adding object to hierarchy:", error);
            return null;
        }
    }

    /**
     * Fire onNodeAdded event for a node and all its children recursively
     */
    private _fireNodeAddedRecursive(node: IHierarchyNode): void {
        this._onNodeAdded.fire(node);
        for (const child of node.children) {
            this._fireNodeAddedRecursive(child);
        }
    }

    /**
     * Fire onNodeRemoved event for a node and all its children recursively
     */
    private _fireNodeRemovedRecursive(node: IHierarchyNode): void {
        this._onNodeRemoved.fire(node);
        for (const child of node.children) {
            this._fireNodeRemovedRecursive(child);
        }
    }


    // #region Remove Node
    remove(object: object): void {

        const node = isHierarchyNode(object) ? object : this.find(object);
        if (!node) {
            this.logger.warn("HierarchyService.remove: Object not found in hierarchy:", object);
            return;
        }

        // Check if the node is a root
        let isRoot = false;
        for (let root of this.roots) {
            if (root === node) {
                const index = this.roots.indexOf(root);
                if (index !== -1) {
                    this.roots.splice(index, 1);
                    isRoot = true;
                    break;
                }
            }
        }

        if (node.parent) {
            // removeChild will trigger onDidChange which fires onNodeRemoved via the listener
            node.parent.removeChild(node);
        } else if (isRoot) {
            // Root node without parent - fire events directly since no onDidChange will be triggered
            this._fireNodeRemovedRecursive(node);
            this._unregisterNode(node);
        }

        this._onDidChange.fire({ hierarchy: this });
    }

    clear(): void {
        for (let i = this.roots.length - 1; i >= 0; i--) {
            const root = this.roots[i];
            // Fire onNodeRemoved for root and all children (before unregistering)
            this._fireNodeRemovedRecursive(root);
            this._unregisterNode(root);
            root.dispose();
            this.roots.splice(i, 1);
        }
        this.roots.length = 0;
        this._onDidChange.fire({ hierarchy: this });
    }


    private readonly nodeDisposeables: WeakMap<IHierarchyNode, IDisposable> = new WeakMap();
    private readonly lookupCacheReference: WeakMap<object, IHierarchyNode> = new WeakMap();
    private readonly lookupCacheId: Map<string, IHierarchyNode> = new Map();

    private _registerNode(node: IHierarchyNode): void {
        if (this.lookupCacheReference.has(node.owner)) {
            return;
        }
        this.lookupCacheReference.set(node.owner, node);
        this.lookupCacheId.set(node.id, node);

        this.nodeDisposeables.set(node, node.onDidChange(evt => {
            if (evt.added) {
                this._registerNode(evt.added);
                this._addedBuffer.add(evt.added);
                this._removedBuffer.delete(evt.added);
                // Fire onNodeAdded for the added node and all its children
                this._fireNodeAddedRecursive(evt.added);
                this.evaluateHierarchyStructureChanged();
            }
            if (evt.removed) {
                // Fire onNodeRemoved for the removed node and all its children (before unregistering)
                this._fireNodeRemovedRecursive(evt.removed);
                this._unregisterNode(evt.removed);
                this._removedBuffer.add(evt.removed);
                this._addedBuffer.delete(evt.removed);
                this.evaluateHierarchyStructureChanged();
            }
        }));

        // register children
        for (const ch of node.children) {
            this._registerNode(ch);
        }
    }
    private _unregisterNode(node: IHierarchyNode): void {
        if (!this.lookupCacheReference.has(node.owner)) {
            return;
        }
        const obj = node.owner;
        if (this.lookupCacheReference.has(obj)) {
            this.lookupCacheReference.delete(obj);
        }
        if (this.lookupCacheId.has(node.id)) {
            this.lookupCacheId.delete(node.id);
        }
        for (const ch of node.children) {
            this._unregisterNode(ch);
        }
        const evts = this.nodeDisposeables.get(node);
        if (evts) {
            evts.dispose();
            this.nodeDisposeables.delete(node);
        }
    }



    private _structureChangedThrottler = new ThrottledDelayer<void>(100);
    private readonly _addedBuffer: Set<IHierarchyNode> = new Set();
    private readonly _removedBuffer: Set<IHierarchyNode> = new Set();

    private evaluateHierarchyStructureChanged() {
        if (!this._structureChangedThrottler.isTriggered()) {
            this._structureChangedThrottler.trigger(async () => {
                let anyChanged = this._addedBuffer.size > 0 && Array.from(this._addedBuffer).some(n => !HierarchyNode.hasFlag(n, NodeFlags.HideInHierarchy));
                anyChanged ||= this._removedBuffer.size > 0 && Array.from(this._removedBuffer).some(n => !HierarchyNode.hasFlag(n, NodeFlags.HideInHierarchy));
                if (anyChanged) {
                    // TODO: ideally we would have a list of added/removed nodes here (maybe on demand via a Lazy?).
                    this.logger.info("Hierarchy structure changed. Added: " + this._addedBuffer.size + ", Removed: " + this._removedBuffer.size);
                    this._onDidChange.fire({ hierarchy: this });
                }
                else {
                    this.logger.debug("Hierarchy structure changed, but no visible nodes were added or removed.");
                }
                this._addedBuffer.clear();
                this._removedBuffer.clear();
            });
        }
    }


    // #region Navigate
    navigate(direction: "up" | "down" | "left" | "right"): IHierarchyNode | null {

        const previousSelected = this.getSelectedNodes();
        const node: IHierarchyNode | null = previousSelected[0] || this.roots[0] || null;
        if (!node) {
            this.logger.debug('No node selected and no roots available for navigation.');
            return null;
        }

        this.logger.debug('Current Node:', node?.name);
        let newNode: IHierarchyNode | null = null;
        let shouldExpand = true;
        // TODO: this logic should probably go into the hierarchyservice itself since it's not really command-specific 
        // AND the hierarchy has roots that are not connected... the command doesnt need to know that though
        switch (direction) {
            case "down":
            case "right":
                shouldExpand = direction === "right";
                // let traverseInChild = node.hasChildren && (direction === "right" || node.expanded);
                if (node.hasChildren) {
                    const firstChild = node.children.find(c => isHideInHierarchy(c) === false);
                    if (firstChild && (direction === "right" || node.expanded)) {
                        newNode = firstChild; // TODO: ignore in hierarchy nodes that are not visible
                        this.logger.debug('Navigating down to child:', newNode.name);
                    }
                }
                // else try next sibling or uncle/aunt
                if (!newNode && node.parent) {
                    const siblings = node.parent.children;
                    const currentIndex = siblings.indexOf(node);
                    const isLast = currentIndex === siblings.length - 1;
                    if (!isLast) {
                        for (let i = currentIndex + 1; i < siblings.length; i++) {
                            if (newNode) break;
                            if (isHideInHierarchy(siblings[i])) continue;
                            newNode = siblings[i];
                            this.logger.debug('Navigating down to next sibling:', newNode.name);
                        }
                    }

                    let ancestor: IHierarchyNode | null = node.parent;
                    while (ancestor && !newNode) {
                        if (ancestor.parent) {
                            const ancestorSiblings = ancestor.parent.children;
                            const ancestorIndex = ancestorSiblings.indexOf(ancestor);
                            if (ancestorIndex < ancestorSiblings.length - 1) {
                                const ancestorNode = ancestorSiblings[ancestorIndex + 1];
                                newNode = ancestorNode;
                                this.logger.debug('Navigating down to next uncle/aunt:', newNode.name);
                                break;
                            }
                        }
                        ancestor = ancestor.parent;
                    }
                }

                if (!newNode) {
                    const rootIndex = this.roots.indexOf(node);
                    if (rootIndex >= 0 && rootIndex < this.roots.length - 1) {
                        newNode = this.roots[rootIndex + 1];
                    }
                }
                break;
            case "up":
            case "left":
                if (node.parent) {
                    const index = node.parent.children.indexOf(node);
                    if (index <= 0) {
                        newNode = node.parent;
                        shouldExpand = false;
                    }
                    else {
                        // go to previous sibling's last child (if expanded), or the sibling itself
                        let sibling = node.parent.children[index - 1];
                        while (sibling.hasChildren && sibling.expanded) {
                            sibling = sibling.children[sibling.children.length - 1];
                        }
                        newNode = sibling;
                        shouldExpand = false;
                    }
                }
                else {
                    const rootIndex = this.roots.indexOf(node);
                    if (rootIndex > 0) {
                        newNode = this.roots[rootIndex - 1];
                    }
                }
                break;
        }

        // Expand/collapse current node if needed
        if (node.expanded && direction === "left") {
            node.setWithoutNotify("expanded", false);
            node.notifyChanged();
        }
        else if (!node.expanded && (direction === "right")) {
            node.setWithoutNotify("expanded", true);
            node.notifyChanged();
        }

        // Set new selection
        if (newNode) {
            previousSelected.forEach(n => {
                n.setWithoutNotify("selected", false);
                n.notifyChanged();
            });

            newNode.setWithoutNotify("selected", true);
            if (shouldExpand) newNode.setWithoutNotify("expanded", true);
            this.logger.debug('New selected node:', newNode.name);

            newNode.notifyChanged();
            this._onSelectedNodesDidChange.fire({ selection: [newNode] });

        }

        return newNode;
    }

}



// #region HierarchyNode




export function isHideInHierarchy(node: unknown): boolean {
    if (!isHierarchyNode(node)) {
        if (typeof node === 'object') {
            node = HierarchyNode.get(node as Record<string | symbol, any>);
            if (!node) return false;
            if (!isHierarchyNode(node)) return false;
        }
        else {
            return false;
        }
    }

    return (node.flags & NodeFlags.HideInHierarchy) !== 0;
}


export const _flagsMap = new WeakMap<object, NodeFlags>();

export class HierarchyNode extends Disposable implements IHierarchyNode {

    /**
     * Gets the HierarchyNode associated with the given object, if any.
     * @param object The object to get the HierarchyNode for.
     * @returns The HierarchyNode or null if none is associated.
     */
    static get(object: object | null | undefined): HierarchyNode | null {
        if (!object) return null;
        if (isHierarchyNode(object)) return object as HierarchyNode;
        return internalGetNode(object);
    }

    static setFlags(object: object, flags: NodeFlags): void {
        if (!object) return;
        const node = HierarchyNode.get(object);
        if (node) {
            node.flags = flags;
            return;
        }
        _flagsMap.set(object, flags);
    }

    static enableFlag(object: object, flags: NodeFlags): void {
        if (!object) return;
        const node = HierarchyNode.get(object);
        if (node) {
            node.flags = node.flags | flags;
            return;
        }
        const existingFlags = _flagsMap.get(object) || NodeFlags.None;
        _flagsMap.set(object, existingFlags | flags);
    }
    static disableFlag(object: object, flags: NodeFlags): void {
        if (!object) return;
        const node = HierarchyNode.get(object);
        if (node) {
            node.flags = node.flags & ~flags;
            return;
        }
        const existingFlags = _flagsMap.get(object) || NodeFlags.None;
        _flagsMap.set(object, existingFlags & ~flags);
    }
    static hasFlag(object: object | null | undefined, flag: NodeFlags): boolean {
        if (!object) return false;
        const node = HierarchyNode.get(object);
        if (node) {
            return (node.flags & flag) !== 0;
        }
        const existingFlags = _flagsMap.get(object) || NodeFlags.None;
        return (existingFlags & flag) !== 0;
    }



    readonly id: string;
    readonly children: HierarchyNode[];
    readonly attachments: IAttachment[] = [];

    get hasChildren() {
        return this.children.length > 0 && this.children.some(child => (child.flags & NodeFlags.HideInHierarchy) === 0);
    }

    private _flags: NodeFlags = NodeFlags.None;
    get flags(): NodeFlags { return this._flags; }
    set flags(value: NodeFlags) {
        if (value === this._flags) return;
        this._flags = value;
        this._notifyChanged();
    }


    get parent(): HierarchyNode | null { return this._parent; }


    get owner() {
        return this._source;
    }

    get type() { return this._type; }

    get name(): string { return this._name; }
    set name(value: string) {
        if (value === this._name) return;
        this._name = value;
        this._notifyChanged();
    }

    get label() { return this._label; }
    set label(value: string) {
        if (value === this._label) return;
        this._label = value;
        this._notifyChanged();
    }
    private _label: string = '';

    get selected() { return this._selected; }
    set selected(value: boolean) {
        if (value === this._selected) return;
        this._selected = value;
    }
    get expanded() { return this._expanded; }
    set expanded(value: boolean) {
        if (value === this._expanded) return;
        this._expanded = value;
        this._notifyChanged();
    }

    get visible() {
        if (this._source && "visible" in this._source) {
            return (this._source as any).visible;
        }
        return true;
    }

    private _icon?: string;
    get icon() {
        return this._icon;
    }
    set icon(value: string | undefined) {
        if (value === this._icon) return;
        this._icon = value;
        this._notifyChanged();
    }

    searchTags: string[] = [];

    setWithoutNotify<T extends keyof Pick<HierarchyNode, "name" | "expanded" | "selected">>(key: T, value: HierarchyNode[T]): void {
        switch (key) {
            case "name":
                this._name = value as string;
                break;
            case "expanded":
                this._expanded = value as boolean;
                break;
            case "selected":
                this._selected = value as boolean;
                break;
        }
    }

    private readonly _selectionChanged = new Emitter<{ node: HierarchyNode, previous: boolean }>();
    get onSelectionDidChange() { return this._selectionChanged.event; }


    private _notifyChanged(node: HierarchyNode = this) {
        this._didChange.fire({ node });
    }
    notifyChanged = () => this._notifyChanged(this);


    private readonly _didChange = new Emitter<HierarchyNodeChangedEvent>();
    readonly onDidChange = this._didChange.event;

    private readonly _disposed = new Emitter<void>();
    readonly onDidDispose = this._disposed.event;


    private _name: string;
    private readonly _source: object;
    private readonly _type: string;
    private _selected: boolean = false;
    private _parent: HierarchyNode | null = null;
    private _expanded: boolean = true;
    private _visible: boolean = true;

    constructor(init: {
        object: object,
        id: string,
        name: string,
        type?: string,
        parent?: HierarchyNode | null,
        expanded?: boolean,
    },
        // private readonly propertyProvider?: IPropertyService

    ) {
        super();
        this.id = init.id;
        this._name = init.name;
        this._label = init.name;
        this._source = init.object;
        this._expanded = init.expanded ?? true;
        this.children = [];
        this._type = init.type || "unknown";
        this._parent?.addChild(this);

        const flags = _flagsMap.get(init.object) || NodeFlags.None;
        this.flags = flags;
        _flagsMap.delete(init.object);

        internalAttachNode(this, init.object);
    }

    addChild(child: HierarchyNode, index: number = -1) {
        if (child._parent === this) {
            // the node is already a child
            return;
        }
        // remove from previous parent (if any)
        if (child._parent) {
            child._parent.removeChild(child);
        }
        child._parent = this;
        if (index >= 0 && index < this.children.length) {
            this.children.splice(index, 0, child);
        } else {
            this.children.push(child);
        }
        this._didChange.fire({ node: this, added: child });
    }

    removeChild(child: HierarchyNode) {
        if (child._parent !== this) {
            // not a child of this node
            return;
        }
        child._parent = null;
        const index = this.children.indexOf(child);
        if (index !== -1) {
            this.children.splice(index, 1);
            this._didChange.fire({ node: this, removed: child });
        }
    }

    remove() {
        if (this._parent) {
            this._parent.removeChild(this);
            this._parent = null;
            this._notifyChanged();
        }
    }

    dispose(): void {
        super.dispose();
        for (const child of this.children) {
            child.dispose();
        }
        this._disposed.fire();
    }

    // @TODO: cache path?
    get path() {
        let parts: string[] = [];
        let current: HierarchyNode | null = this;
        while (current) {
            parts.unshift(current.name || current.type);
            current = current.parent;
        }
        return parts.join("/");
    }

}


const $node = Symbol("needle-editor:node");

export function internalAttachNode(node: HierarchyNode, target: Record<string | symbol, any>) {
    target[$node] = node;
}
export function internalGetNode(target: Record<string | symbol, any>): HierarchyNode | null {
    return target[$node] || null;
}
